import { PropsOf } from '@emotion/react';
import {
  ArrowBackIosNew as ArrowBack,
  RefreshRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { PAGE_PADDING } from '../../constants/ui';

interface Props {
  containerClassName?: string;
  mainContainerProps?: PropsOf<typeof Stack>;
  containerProps?: PropsOf<typeof Box>;
  title?: string;
  isLoading?: boolean;
  withoutChildContainer?: boolean;
  childContainerProps?: PropsOf<typeof Box>;
  headerIsHidden?: boolean;
  refresh?: () => void | Promise<void>;
  guard?: {
    isAllowed: boolean;
    redirectTo?: string;
  };
  topSection?: React.ReactNode;
  onBack?: () => void;
}

const HEADER_GAP = 1;

function ChildContainer({
  children,
  withoutChildContainer,
  childContainerProps,
}: {
  children: React.ReactNode;
  withoutChildContainer: boolean;
  childContainerProps?: PropsOf<typeof Box>;
}) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (withoutChildContainer) return <>{children}</>;
  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      gap={3}
      {...childContainerProps}>
      {children}
    </Box>
  );
}

function PageLayout({
  children,
  isLoading,
  title,
  containerClassName,
  mainContainerProps,
  containerProps,
  childContainerProps,
  withoutChildContainer = false,
  headerIsHidden = false,
  refresh,
  guard,
  topSection,
  onBack,
}: PropsWithChildren<Props>) {
  const navigate = useNavigate();

  React.useEffect(() => {
    if (guard && !guard.isAllowed) {
      navigate(guard.redirectTo || '/');
    }
  }, [guard, navigate]);

  return (
    <Stack width="100%" {...(mainContainerProps ?? {})}>
      {topSection}
      <Box
        className={containerClassName}
        width="100%"
        display="flex"
        flexDirection="column"
        gap={3}
        p={PAGE_PADDING}
        boxSizing="border-box"
        {...containerProps}>
        {!headerIsHidden && (
          <Box width="100%" display="flex" alignItems="center" gap={HEADER_GAP}>
            <Stack
              color="black"
              direction="row"
              alignItems="center"
              justifyContent="start"
              gap={HEADER_GAP}
              p={0}
              m={0}
              maxWidth="calc(100% - 40px)" // 40px is the width of the loading spinner & refresh button
              component={onBack ? Button : 'div'}
              onClick={onBack}
              sx={{
                ':hover': {
                  bgcolor: 'transparent',
                },
              }}>
              {onBack && <ArrowBack />}
              {!!title && (
                <Typography noWrap variant="h1" color="inherit">
                  {title}
                </Typography>
              )}
            </Stack>
            {isLoading && <CircularProgress color="primary" size={20} />}
            {refresh && !isLoading && (
              <Button
                variant="link"
                onClick={refresh}
                sx={{
                  ml: 'auto',
                  minWidth: 0,
                }}>
                <RefreshRounded color="primary" width={20} />
              </Button>
            )}
          </Box>
        )}
        <ChildContainer
          withoutChildContainer={withoutChildContainer}
          childContainerProps={childContainerProps}>
          {children}
        </ChildContainer>
      </Box>
    </Stack>
  );
}

export default PageLayout;
